import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Plus, Search, Edit2, Check, X } from "lucide-react";

const CATEGORIES = ["Dry Goods", "Dairy", "Produce", "Meat & Seafood", "Beverages", "Coffee", "Bakery", "Packaging", "Cleaning", "Consumables"];
const BASE_UOMS = ["kg", "g", "L", "ml", "pc"];

export default function ItemMaster({ items, onRefresh }) {
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // sku being edited
  const [form, setForm] = useState({
    name: "", category: "Dry Goods", base_uom: "kg",
    purchase_uom_label: "", base_per_purchase: "", gst_rate: 0,
    perishable: false, shelf_life_days: "",
  });
  const [saving, setSaving] = useState(false);

  const filtered = items.filter(i => {
    if (catFilter && i.category !== catFilter) return false;
    if (!q) return true;
    const needle = q.toLowerCase();
    return i.name.toLowerCase().includes(needle) || i.sku.toLowerCase().includes(needle);
  });

  const resetForm = () => {
    setForm({ name: "", category: "Dry Goods", base_uom: "kg", purchase_uom_label: "", base_per_purchase: "", gst_rate: 0, perishable: false, shelf_life_days: "" });
    setCreating(false);
    setEditing(null);
  };

  const startEdit = (item) => {
    setForm({
      name: item.name,
      category: item.category,
      base_uom: item.base_uom,
      purchase_uom_label: item.purchase_uom_label || "",
      base_per_purchase: item.base_per_purchase || "",
      gst_rate: item.gst_rate || 0,
      perishable: item.perishable || false,
      shelf_life_days: item.shelf_life_days || "",
    });
    setEditing(item.sku);
    setCreating(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category,
        base_uom: form.base_uom,
        purchase_uom_label: form.purchase_uom_label || null,
        base_per_purchase: form.base_per_purchase ? Number(form.base_per_purchase) : null,
        gst_rate: Number(form.gst_rate) || 0,
        perishable: form.perishable,
        shelf_life_days: form.shelf_life_days ? Number(form.shelf_life_days) : null,
      };
      if (editing) {
        await api.patch(`/staff/inventory/v2/items/${editing}`, payload);
        toast.success("Item updated");
      } else {
        await api.post("/staff/inventory/v2/items", payload);
        toast.success("Item created");
      }
      resetForm();
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="item-master" className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--ink-soft))]" />
          <input
            data-testid="im-search"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search items..."
            className="tbb-inp w-full pl-9"
          />
        </div>
        <button data-testid="im-new" onClick={() => { resetForm(); setCreating(true); }} className="btn-brass !py-2 !px-4 text-sm whitespace-nowrap">
          <Plus size={14} /> New
        </button>
      </div>

      {/* Category filter */}
      <div className="flex gap-1 overflow-x-auto pb-1 -mx-4 px-4">
        <button onClick={() => setCatFilter("")} className={`px-3 py-1 text-[10px] uppercase tracking-wider rounded-full whitespace-nowrap ${!catFilter ? "bg-[hsl(var(--forest))] text-[hsl(var(--bg))]" : "bg-[hsl(var(--bg-2))] text-[hsl(var(--ink-soft))]"}`}>All</button>
        {CATEGORIES.map(c => (
          <button key={c} onClick={() => setCatFilter(c === catFilter ? "" : c)} className={`px-3 py-1 text-[10px] uppercase tracking-wider rounded-full whitespace-nowrap ${catFilter === c ? "bg-[hsl(var(--forest))] text-[hsl(var(--bg))]" : "bg-[hsl(var(--bg-2))] text-[hsl(var(--ink-soft))]"}`}>{c}</button>
        ))}
      </div>

      {/* Create/edit form */}
      {creating && (
        <div data-testid="im-form" className="p-4 border border-[hsl(var(--brass))] bg-[hsl(var(--bg-2))] space-y-3">
          <div className="text-sm font-semibold">{editing ? `Edit ${editing}` : "New item"}</div>
          <input data-testid="im-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Item name *" className="tbb-inp w-full" />
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
              Category
              <select data-testid="im-category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="tbb-inp mt-1 w-full">
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
              Base UoM
              <select data-testid="im-uom" value={form.base_uom} onChange={e => setForm({ ...form, base_uom: e.target.value })} className="tbb-inp mt-1 w-full">
                {BASE_UOMS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </label>
            <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
              Purchase UoM label
              <input data-testid="im-purchase-label" value={form.purchase_uom_label} onChange={e => setForm({ ...form, purchase_uom_label: e.target.value })} placeholder="e.g. Bag 25 kg" className="tbb-inp mt-1 w-full" />
            </label>
            <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
              Base per purchase
              <input type="number" step="0.01" data-testid="im-base-per" value={form.base_per_purchase} onChange={e => setForm({ ...form, base_per_purchase: e.target.value })} placeholder="e.g. 25" className="tbb-inp mt-1 w-full" />
            </label>
            <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
              GST %
              <input type="number" step="0.1" data-testid="im-gst" value={form.gst_rate} onChange={e => setForm({ ...form, gst_rate: e.target.value })} className="tbb-inp mt-1 w-full" />
            </label>
            <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
              Shelf life (days)
              <input type="number" data-testid="im-shelf" value={form.shelf_life_days} onChange={e => setForm({ ...form, shelf_life_days: e.target.value })} className="tbb-inp mt-1 w-full" disabled={!form.perishable} />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="im-perishable" checked={form.perishable} onChange={e => setForm({ ...form, perishable: e.target.checked })} className="w-4 h-4" />
            Perishable
          </label>
          <div className="flex gap-2">
            <button data-testid="im-save" onClick={save} disabled={saving} className="btn-brass !py-2 !px-4 text-sm">
              {saving ? "Saving..." : (editing ? "Save changes" : "Create item")}
            </button>
            <button onClick={resetForm} className="text-sm text-[hsl(var(--ink-soft))]">Cancel</button>
          </div>
        </div>
      )}

      {/* Item list */}
      <div className="border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] divide-y divide-[hsl(var(--rule))]">
        {filtered.length === 0 && <div className="p-6 text-center text-[hsl(var(--ink-soft))]">No items found.</div>}
        {filtered.map(item => (
          <div key={item.sku} data-testid={`im-row-${item.sku}`} className="p-3 flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm truncate">{item.name}</div>
              <div className="text-xs text-[hsl(var(--ink-soft))]">
                <span className="font-mono">{item.sku}</span> · {item.category}
                {item.purchase_uom_label && item.base_per_purchase && (
                  <span className="ml-1 text-[hsl(var(--brass-2))]">· 1 {item.purchase_uom_label} = {item.base_per_purchase} {item.base_uom}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {item.perishable && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[hsl(38_90%_88%)] text-[hsl(35_75%_38%)]">Perishable</span>}
              <button data-testid={`im-edit-${item.sku}`} onClick={() => startEdit(item)} className="p-1.5 text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--forest))]">
                <Edit2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
      <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.55rem 0.75rem;font-size:0.9rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
    </div>
  );
}
